import { ThemeEvents } from '@theme/events';

/**
 * Drives the PDP "Notify me" button for Attentive's back-in-stock waitlist.
 * Markup and the rules for who can join live in snippets/forcast-waitlist.liquid.
 *
 * The shopper sees the theme's own button; Attentive's tag-created button sits
 * in a hidden wrapper and is only ever clicked from here. Two reasons:
 *
 * - Speed. The tag decides which variant its button enrols from its own
 *   product-view event, and after a size change it first re-sends that event
 *   with the size the page loaded on (measured on the live PDP: `?variant=`
 *   moved, the event did not), so its button appeared a second or more late.
 *   The theme button is shown/hidden on `variant:selected` — the moment the
 *   size is tapped, from the picker input's `data-variant-id` — against the
 *   eligibility map, with no network in the way. (`variant:update` only fires
 *   once the section has been re-fetched, ~0.5s later.)
 * - Copy. The tag's button carries the label set in the sign-up unit; the
 *   theme's comes from the locale.
 *
 * The tag still has to be on the selected size before its button is clicked, or
 * a shopper on a sold-out 10 would join the waitlist for the 6. So this file
 * re-sends the product view with the selected variant whenever the tag drifts,
 * and a click waits until the tag agrees before forwarding.
 *
 * Every click is also published as the `forcast:waitlist_join` customer event
 * (see publish()), the way the wishlist publishes its saves, so custom pixels
 * can count back-in-stock requests per product and size.
 */

const ATTN_CONTAINER_ID = 'attn-waitlist-trigger';
const ATTN_BUTTON_ID = 'attn-waitlist-trigger-btn';
const ELIGIBILITY_ID = 'attn-waitlist-eligibility';
const BUTTON_SELECTOR = '.forcast-waitlist__button';

const TAG_WAIT_MS = 10000;
const SYNC_WAIT_MS = 4000;
const ENROL_WAIT_MS = 3000;

/** Variants enrolled this page view (the tag disabled its button for them). */
const enrolled = new Set();

/** @returns {HTMLButtonElement | null} */
const themeButton = () => document.querySelector(BUTTON_SELECTOR);
/** @returns {HTMLElement | null} */
const attnContainer = () => document.getElementById(ATTN_CONTAINER_ID);
/** @returns {HTMLButtonElement | null} */
const attnButton = () => /** @type {HTMLButtonElement | null} */ (document.getElementById(ATTN_BUTTON_ID));
const tagLoaded = () => typeof window.attentive?.analytics?.productView === 'function';

/**
 * Parsed once per eligibility map. Keyed on the JSON text, not the element:
 * changing colour morphs `main`, and the morph keeps the element (same id) and
 * only rewrites its text, so a per-element cache kept answering for the colour
 * the page loaded on.
 */
let eligibility = { text: '', ids: /** @type {Record<string, boolean>} */ ({}) };
/** @returns {Record<string, boolean>} */
function eligibleIds() {
  const text = document.getElementById(ELIGIBILITY_ID)?.textContent || '';
  if (text !== eligibility.text) {
    let ids = {};
    try {
      ids = JSON.parse(text || '{}');
    } catch {}
    eligibility = { text, ids };
  }
  return eligibility.ids;
}

/** The variant a just-tapped size resolves to, until variant:update confirms it. */
let pendingVariantId = '';

/** @returns {string | null} */
function selectedVariantId() {
  if (pendingVariantId) return pendingVariantId;
  /** @type {HTMLInputElement | null | undefined} */
  const input = themeButton()?.closest('product-form-component')?.querySelector('input[name="id"]');
  return input?.value || new URL(window.location.href).searchParams.get('variant');
}

const isEligible = (/** @type {string | null} */ id) => Boolean(id && eligibleIds()[id]);

/** The tag has its button, and believes `id` is the selected variant. */
const tagOn = (/** @type {string} */ id) =>
  Boolean(attnButton()) && attnContainer()?.getAttribute('data-variant-id') === id;

/**
 * @param {() => unknown} test
 * @param {number} timeout
 * @returns {Promise<boolean>}
 */
function waitFor(test, timeout) {
  return new Promise((resolve) => {
    const started = Date.now();
    const poll = () => {
      if (test()) return resolve(true);
      if (Date.now() - started > timeout) return resolve(false);
      window.setTimeout(poll, 100);
    };
    poll();
  });
}

/** @param {string} variantId */
function sendProductView(variantId) {
  const el = attnContainer();
  const productView = window.attentive?.analytics?.productView;
  if (!el || typeof productView !== 'function') return;

  productView({
    items: [
      {
        productId: el.dataset.productGid,
        productVariantId: variantId,
        name: el.dataset.productTitle,
        price: { value: el.dataset.productPrice, currency: el.dataset.currency },
      },
    ],
  });
}

let lastSent = { id: '', at: 0 };
/** Point the tag at the selected size if it has drifted off it. */
function syncTag() {
  const id = selectedVariantId();
  if (!id || !isEligible(id) || !tagLoaded() || tagOn(id)) return;
  // The tag's stale view can land after ours; resend, but not in a tight loop.
  if (lastSent.id === id && Date.now() - lastSent.at < 500) return;
  lastSent = { id, at: Date.now() };
  sendProductView(id);
}

/** Show the theme button for an eligible size, in its enrolled state if joined. */
function render() {
  const button = themeButton();
  if (!button) return;

  const id = selectedVariantId();
  const eligible = isEligible(id);
  button.hidden = !eligible;
  if (!eligible || !id) return;

  const joined = enrolled.has(id);
  button.disabled = joined;
  const label = joined ? button.dataset.enrolledLabel : button.dataset.label;
  if (label && button.textContent !== label) button.textContent = label;
}

/** @param {MouseEvent} event */
async function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(BUTTON_SELECTOR);
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const id = selectedVariantId();
  if (!id || !isEligible(id)) return;

  button.setAttribute('aria-busy', 'true');
  /** @type {'enrolled' | 'pending' | 'unavailable'} */
  let outcome = 'unavailable';
  try {
    if (!(await waitFor(() => tagLoaded() && attnButton(), TAG_WAIT_MS))) return;
    if (!tagOn(id)) {
      sendProductView(id);
      if (!(await waitFor(() => tagOn(id), SYNC_WAIT_MS))) return;
    }
    // The shopper may have switched size while we waited.
    if (selectedVariantId() !== id) return;

    const attn = attnButton();
    if (!attn) return;
    if (!attn.disabled) {
      // Enrols the variant; opens the sign-up unit unless the shopper is
      // already a text subscriber, in which case the tag disables its button.
      attn.click();
      outcome = 'pending';
      await waitFor(() => attn.disabled, ENROL_WAIT_MS);
    }
    if (attn.disabled) {
      enrolled.add(id);
      outcome = 'enrolled';
    }
  } finally {
    button.removeAttribute('aria-busy');
    render();
    publish(id, outcome);
  }
}

/**
 * Tells analytics the shopper asked to be notified, once per click, with how
 * far it got: `enrolled` (the tag confirmed), `pending` (the tag's button was
 * clicked — its sign-up unit opened, or the confirmation outran the wait) or
 * `unavailable` (the tag never loaded, never agreed on the size, or the size
 * changed meanwhile). Custom events reach custom pixels only; Sage's pixel
 * stores this one as `back_in_stock_requested`. Best effort: it must never
 * stop a shopper from joining.
 *
 * @param {string} variantId
 * @param {'enrolled' | 'pending' | 'unavailable'} outcome
 */
function publish(variantId, outcome) {
  try {
    const el = attnContainer();
    window.Shopify?.analytics?.publish?.('forcast:waitlist_join', {
      productId: el?.dataset.productGid || '',
      variantId,
      title: el?.dataset.productTitle || '',
      price: el?.dataset.productPrice || '',
      currency: el?.dataset.currency || '',
      outcome,
    });
  } catch {
    // Analytics is best effort; the waitlist must work without it.
  }
}

/** @param {Event} event */
function onVariantSelected(event) {
  const picker = event.target instanceof Element ? event.target : null;
  const optionValueId = /** @type {CustomEvent} */ (event).detail?.resource?.id;
  if (!picker || !optionValueId) return;
  /** @type {HTMLElement | null} */
  const option = picker.querySelector(`[data-option-value-id="${CSS.escape(String(optionValueId))}"]`);
  // No data-variant-id: that combination has no variant (or is another
  // colourway's product). Leave it to variant:update.
  const variantId = option?.dataset.variantId;
  if (!variantId) return;
  pendingVariantId = variantId;
  render();
  syncTag();
}

function onVariantUpdate() {
  pendingVariantId = '';
  render();
  // The buy buttons may be re-rendered after the event; render the new markup too.
  window.requestAnimationFrame(render);
  window.setTimeout(render, 150);
  syncTag();
}

/* Re-point the tag whenever it moves its container to another variant (it
   rewrites or removes data-variant-id on every product view, stale or not) or
   attaches its button. */
new MutationObserver(syncTag).observe(document.body, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['data-variant-id'],
});

document.addEventListener(ThemeEvents.variantSelected, onVariantSelected);
document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
document.addEventListener('click', onClick);

render();
waitFor(tagLoaded, TAG_WAIT_MS).then((ready) => {
  if (ready) syncTag();
});
